import { MarketStatus } from '@ps/isp-web-services-types';
import produce from 'immer';

import type { MarketPollingData } from '@swf/sports/types/market';
import updateMarket from '@swf/sports/utils/updateMarket';

import { defaultState } from './defaultState';
import type { RacePageResponseWithRaceId } from './types';

type State = typeof defaultState;

export const reducers = {
  clear: (): State => produce(defaultState, (draft) => draft),
  racePageRequested: (
    state: State,
    { isBackgroundLoading = false }: { isBackgroundLoading?: boolean } = {}
  ): State => ({
    ...state,
    isLoading: !isBackgroundLoading,
    errorCode: undefined,
    hasPreloaded: false
  }),
  dataLoadSuccess: (state: State, payload: RacePageResponseWithRaceId): State => {
    if (state.cachedRace?.raceId !== payload.raceId) {
      return {
        ...state,
        ...payload,
        isLoading: false,
        hasPreloaded: true,
        errorCode: undefined,
        cachedRace: payload.races[payload.raceId],
        cachedRaces: Object.values(payload.races)
      };
    }

    return {
      ...state,
      ...payload,
      isLoading: false,
      hasPreloaded: true,
      errorCode: undefined
    };
  },
  dataLoadFailure: (state: State, payload: number): State => ({
    ...defaultState,
    isLoading: false,
    hasPreloaded: true,
    errorCode: payload,
    cachedRace: state.cachedRace,
    cachedRaces: state.cachedRaces
  }),
  marketsUpdated: (state: State, { data: marketUpdates, closed }: MarketPollingData) =>
    produce(state, (draft) => {
      Object.values(draft.markets).forEach((market) => {
        if (!market) return;

        if (closed.includes(market.marketId)) {
          market.marketStatus = MarketStatus.CLOSED;
          return;
        }

        const marketUpdate = marketUpdates[market.marketId];

        if (marketUpdate) updateMarket(market, marketUpdate);
      });

      return draft;
    })
};
